const ASSISTANT_MESSAGE_MUTATION_SCHEMA = 'attunewise.assistant_message_mutation.v1'

const ASSISTANT_MESSAGE_MUTATION_TYPES = Object.freeze({
  SET_CONTENT: 'set_content',
  SELECT_REPLAY: 'select_replay',
  DELETE_REPLY: 'delete_reply',
  DELETE_TOOL_CALL: 'delete_tool_call',
  APPEND_TRANSCRIPT_ENTRIES: 'append_transcript_entries'
})

const REPLY_STATE_FIELDS = Object.freeze([
  'content',
  'contentLength',
  'storage',
  'usage',
  'addedToTranscript',
  'phase',
  'hiddenSignatureStoragePath',
  'hiddenSignatureCount',
  'hiddenSignatureContentLength',
  'rawContentLength'
])

class AssistantMessageMutationError extends Error {
  constructor(code, message) {
    super(message)
    this.name = 'AssistantMessageMutationError'
    this.code = code
  }
}

const mutationError = (code, message) => {
  throw new AssistantMessageMutationError(code, message)
}

const clonePlainValue = value => {
  if (Array.isArray(value)) return value.map(clonePlainValue)
  if (!value || typeof value !== 'object') return value
  if (Object.getPrototypeOf(value) !== Object.prototype) return value
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clonePlainValue(item)]))
}

const normalizeAssistantReplyTarget = target => {
  if (!target || typeof target !== 'object') {
    return mutationError('invalid_reply_target', 'assistant reply target is required')
  }
  if (target.kind === 'top-level') return { kind: 'top-level' }
  if (target.kind === 'model' && typeof target.model === 'string' && target.model.trim()) {
    return { kind: 'model', model: target.model.trim() }
  }
  return mutationError('invalid_reply_target', 'assistant reply target must identify the top-level reply or a model reply')
}

const assistantReplyTargetFor = (message, reply) => {
  if (!message || !reply) {
    return mutationError('invalid_reply_target', 'assistant message and reply are required')
  }
  if (message === reply) return { kind: 'top-level' }
  const replies = Array.isArray(message.models) ? message.models : []
  const byIdentity = replies.find(candidate => candidate === reply)
  if (byIdentity) return { kind: 'model', model: byIdentity.model }
  const byModel = replies.find(candidate => candidate && candidate.model === reply.model)
  if (byModel) return { kind: 'model', model: byModel.model }
  if (message.model && message.model === reply.model) return { kind: 'top-level' }
  return mutationError('invalid_reply_target', `assistant reply is not part of message ${message.id || ''}`.trim())
}

const resolveAssistantReply = (message, target) => {
  const normalized = normalizeAssistantReplyTarget(target)
  if (!message || message.role !== 'assistant') {
    return mutationError('not_assistant_message', 'assistant message mutation requires an assistant document')
  }
  if (normalized.kind === 'top-level') {
    return { target: normalized, reply: message, modelIndex: -1 }
  }
  const models = Array.isArray(message.models) ? message.models : []
  const modelIndex = models.findIndex(reply => reply && reply.model === normalized.model)
  if (modelIndex < 0) {
    return mutationError('assistant_reply_not_found', `assistant model reply not found: ${normalized.model}`)
  }
  return { target: normalized, reply: models[modelIndex], modelIndex }
}

const captureReplyState = reply => {
  const state = {}
  for (const field of REPLY_STATE_FIELDS) {
    if (reply && reply[field] !== undefined) state[field] = clonePlainValue(reply[field])
  }
  return state
}

const applyReplyState = (reply, state) => {
  for (const field of REPLY_STATE_FIELDS) {
    if (state && state[field] !== undefined) {
      reply[field] = clonePlainValue(state[field])
    } else {
      delete reply[field]
    }
  }
  delete reply.stream
  delete reply.corrected
  return reply
}

const applyReplyReplaySelection = (reply, replayIndex) => {
  if (!reply || !Array.isArray(reply.replays)) return false
  const targetIndex = Number(replayIndex)
  if (!Number.isInteger(targetIndex) || targetIndex < 0 || targetIndex >= reply.replays.length) return false
  const replays = reply.replays.map(item => item === null ? null : clonePlainValue(item))
  const selected = replays[targetIndex]
  const currentIndex = replays.findIndex(value => value === null)
  if (!selected || currentIndex < 0) return false
  replays[currentIndex] = captureReplyState(reply)
  applyReplyState(reply, selected)
  replays[targetIndex] = null
  reply.replays = replays
  return true
}

const clearReply = reply => {
  applyReplyState(reply, { content: '' })
  delete reply.replays
  return reply
}

const deleteActiveReply = reply => {
  if (!Array.isArray(reply.replays)) {
    clearReply(reply)
    return { promotedReplay: false }
  }
  const replays = reply.replays.map(item => item === null ? null : clonePlainValue(item))
  const currentIndex = replays.findIndex(value => value === null)
  if (currentIndex < 0) {
    clearReply(reply)
    return { promotedReplay: false }
  }
  replays.splice(currentIndex, 1)
  let selectedIndex = Math.min(currentIndex, replays.length - 1)
  while (selectedIndex >= 0 && !replays[selectedIndex]) selectedIndex -= 1
  if (selectedIndex < 0) {
    clearReply(reply)
    return { promotedReplay: false }
  }
  const selected = replays[selectedIndex]
  applyReplyState(reply, selected)
  replays[selectedIndex] = null
  reply.replays = replays
  return { promotedReplay: true, replayIndex: selectedIndex }
}

const entryMessage = entry => {
  if (!entry || entry.compaction) return null
  if (entry.message && typeof entry.message === 'object') return entry.message
  if (entry.role || entry.tool_calls || entry.tool_call_id) return entry
  return null
}

const hasMessageContent = message => {
  const content = message && message.content
  if (typeof content === 'string') return content.length > 0
  return Array.isArray(content) ? content.length > 0 : Boolean(content)
}

const deleteToolCallFromTranscript = (reply, toolCallId) => {
  const id = String(toolCallId || '')
  if (!id) return mutationError('invalid_tool_call_id', 'tool call id is required')
  if (!Array.isArray(reply.addedToTranscript)) {
    return mutationError('assistant_tool_call_not_found', `tool call not found: ${id}`)
  }
  let removedCalls = 0
  let removedResults = 0
  const next = []
  for (const originalEntry of reply.addedToTranscript) {
    const entry = clonePlainValue(originalEntry)
    const message = entryMessage(entry)
    if (!message) {
      next.push(entry)
      continue
    }
    if (String(message.tool_call_id || '') === id) {
      removedResults += 1
      continue
    }
    if (Array.isArray(message.tool_calls)) {
      const filtered = message.tool_calls.filter(toolCall => String(toolCall && toolCall.id || '') !== id)
      removedCalls += message.tool_calls.length - filtered.length
      if (filtered.length !== message.tool_calls.length) {
        if (filtered.length) {
          message.tool_calls = filtered
        } else {
          delete message.tool_calls
          if (!hasMessageContent(message)) continue
        }
      }
    }
    next.push(entry)
  }
  if (!removedCalls && !removedResults) {
    return mutationError('assistant_tool_call_not_found', `tool call not found: ${id}`)
  }
  reply.addedToTranscript = next
  return { toolCallId: id, removedCalls, removedResults }
}

const normalizeAssistantMessageMutation = request => {
  if (!request || typeof request !== 'object') {
    return mutationError('invalid_assistant_mutation', 'assistant message mutation request is required')
  }
  if (request.schema !== ASSISTANT_MESSAGE_MUTATION_SCHEMA) {
    return mutationError('invalid_assistant_mutation_schema', `unsupported assistant message mutation schema: ${request.schema || ''}`)
  }
  const messageId = String(request.messageId || '').trim()
  const mutationId = String(request.mutationId || '').trim()
  if (!messageId) return mutationError('invalid_assistant_message_id', 'assistant message id is required')
  if (!mutationId) return mutationError('invalid_assistant_mutation_id', 'assistant mutation id is required')
  const target = normalizeAssistantReplyTarget(request.target)
  const operation = request.operation
  if (!operation || typeof operation !== 'object') {
    return mutationError('invalid_assistant_mutation_operation', 'assistant mutation operation is required')
  }
  const type = String(operation.type || '')
  switch (type) {
    case ASSISTANT_MESSAGE_MUTATION_TYPES.SET_CONTENT:
      if (typeof operation.content !== 'string') {
        return mutationError('invalid_assistant_content', 'assistant content must be a string')
      }
      return { schema: request.schema, messageId, mutationId, target, operation: { type, content: operation.content } }
    case ASSISTANT_MESSAGE_MUTATION_TYPES.SELECT_REPLAY: {
      const replayIndex = Number(operation.replayIndex)
      if (!Number.isInteger(replayIndex) || replayIndex < 0) {
        return mutationError('invalid_replay_index', 'assistant replay index must be a non-negative integer')
      }
      return { schema: request.schema, messageId, mutationId, target, operation: { type, replayIndex } }
    }
    case ASSISTANT_MESSAGE_MUTATION_TYPES.DELETE_REPLY:
      return { schema: request.schema, messageId, mutationId, target, operation: { type } }
    case ASSISTANT_MESSAGE_MUTATION_TYPES.DELETE_TOOL_CALL: {
      const toolCallId = String(operation.toolCallId || '').trim()
      if (!toolCallId) return mutationError('invalid_tool_call_id', 'tool call id is required')
      return { schema: request.schema, messageId, mutationId, target, operation: { type, toolCallId } }
    }
    case ASSISTANT_MESSAGE_MUTATION_TYPES.APPEND_TRANSCRIPT_ENTRIES: {
      if (!Array.isArray(operation.entries) || !operation.entries.length) {
        return mutationError('invalid_transcript_entries', 'assistant transcript entries are required')
      }
      return {
        schema: request.schema,
        messageId,
        mutationId,
        target,
        operation: { type, entries: clonePlainValue(operation.entries) }
      }
    }
    default:
      return mutationError('unsupported_assistant_mutation', `unsupported assistant mutation operation: ${type}`)
  }
}

const applyAssistantMessageMutation = (message, request) => {
  const command = normalizeAssistantMessageMutation(request)
  const document = clonePlainValue(message)
  const resolved = resolveAssistantReply(document, command.target)
  const reply = resolved.reply
  let effects = {}
  switch (command.operation.type) {
    case ASSISTANT_MESSAGE_MUTATION_TYPES.SET_CONTENT:
      reply.content = command.operation.content
      delete reply.contentLength
      delete reply.storage
      delete reply.hiddenSignatureStoragePath
      delete reply.hiddenSignatureCount
      delete reply.hiddenSignatureContentLength
      delete reply.rawContentLength
      delete reply.stream
      delete reply.corrected
      effects = { contentChanged: true }
      break
    case ASSISTANT_MESSAGE_MUTATION_TYPES.SELECT_REPLAY:
      if (!applyReplyReplaySelection(reply, command.operation.replayIndex)) {
        return mutationError('assistant_replay_not_selectable', `assistant replay cannot be selected: ${command.operation.replayIndex}`)
      }
      effects = { replaySelected: command.operation.replayIndex }
      break
    case ASSISTANT_MESSAGE_MUTATION_TYPES.DELETE_REPLY:
      effects = { replyDeleted: true, ...deleteActiveReply(reply) }
      break
    case ASSISTANT_MESSAGE_MUTATION_TYPES.DELETE_TOOL_CALL:
      effects = { transcriptChanged: true, ...deleteToolCallFromTranscript(reply, command.operation.toolCallId) }
      break
    case ASSISTANT_MESSAGE_MUTATION_TYPES.APPEND_TRANSCRIPT_ENTRIES:
      reply.addedToTranscript = (reply.addedToTranscript || []).concat(
        clonePlainValue(command.operation.entries)
      )
      effects = {
        transcriptChanged: true,
        transcriptEntriesAppended: command.operation.entries.length
      }
      break
    default:
      return mutationError('unsupported_assistant_mutation', `unsupported assistant mutation operation: ${command.operation.type}`)
  }
  return { command, document, reply, effects }
}

module.exports = {
  ASSISTANT_MESSAGE_MUTATION_SCHEMA,
  ASSISTANT_MESSAGE_MUTATION_TYPES,
  AssistantMessageMutationError,
  REPLY_STATE_FIELDS,
  applyAssistantMessageMutation,
  applyReplyReplaySelection,
  assistantReplyTargetFor,
  captureReplyState,
  clonePlainValue,
  deleteToolCallFromTranscript,
  normalizeAssistantMessageMutation,
  normalizeAssistantReplyTarget,
  resolveAssistantReply
}
